import type { Handler } from 'mitt'
import mitt from 'mitt'
import _api from './_api'
import storage from './storage'
import xhr from './_api/xhr'
import { PLAYER_SETTINGS } from '@common/Types'
import type { ClientId, Me } from '@common/Types'
import { computed, ref } from 'vue'
import Time from '@common/Time'

const nsfwUnblurred = ref(false)
const setNsfwUnblurred = (newValue: boolean): void => {
  nsfwUnblurred.value = newValue
}

const hideAiImages = ref(false)
const setHideAiImages = (newValue: boolean): void => {
  hideAiImages.value = newValue
}

export const useNsfw = () => {
  return {
    nsfwUnblurred,
    setNsfwUnblurred,
  }
}

export const useHideAiImages = () => {
  return {
    hideAiImages,
    setHideAiImages,
  }
}

export const me = ref<null | Me>(null)
export const loggedIn = computed<boolean>(() => !!(me.value && me.value.type === 'user'))
export const isAdmin = computed<boolean>(() => !!(me.value?.groups.includes('admin')))

const eventBus = mitt()

export const onLoginStateChange = (callback: Handler<unknown>) => {
  const offs: (() => void)[] = []
  offs.push(onEvent('login', callback))
  offs.push(onEvent('logout', callback))

  return () => {
    for (const off of offs) {
      off()
    }
  }
}

const onEvent = (evt: string, callback: Handler<unknown>) => {
  eventBus.on(evt, callback)
  return () => {
    eventBus.off(evt, callback)
  }
}

export async function init(): Promise<void> {
  xhr.setClientId(storage.uniq('ID') as ClientId)

  try {
    const localTimestamp = Time.rawTimestamp()

    const res = await _api.pub.me()
    const data = await res.json()

    const serverTimestamp = data.serverTimestamp

    // TODO: for more accurate offset, the server should provide the offset directly
    // this requires the client to send the current client timestamp with the request
    // further improvements could be made by trying to keep this in sync, but it is
    // not likely that the clock drifts that much during a session
    // The Time syncing functionality should maybe also be moved to a separate module
    Time.setServerOffset(serverTimestamp ? serverTimestamp - localTimestamp : 0)

    if ('reason' in data) {
      // console.log('not logged in')
      me.value = null
      setNsfwUnblurred(false)
      setHideAiImages(false)
      eventBus.emit('logout')
    } else {
      // console.log('logged in (reg or guest)')
      xhr.setClientId(data.user.clientId)
      me.value = data.user
      setNsfwUnblurred(data.user.nsfwUnblurred)
      setHideAiImages(data.user.hideAiImages)
      eventBus.emit('login')
    }
  } catch {
    // console.log('not logged in')
    eventBus.emit('logout')
  }
}

export async function logout(): Promise<{ error: string | false }> {
  // remove all relevant data on logout
  storage.remove('ID')
  storage.remove('lastSeenAnnouncement')
  storage.remove(PLAYER_SETTINGS.SOUND_VOLUME)
  storage.remove(PLAYER_SETTINGS.SOUND_ENABLED)
  storage.remove(PLAYER_SETTINGS.OTHER_PLAYER_CLICK_SOUND_ENABLED)
  storage.remove(PLAYER_SETTINGS.COLOR_BACKGROUND)
  storage.remove(PLAYER_SETTINGS.SHOW_TABLE)
  storage.remove(PLAYER_SETTINGS.TABLE_TEXTURE)
  storage.remove(PLAYER_SETTINGS.USE_CUSTOM_TABLE_TEXTURE)
  storage.remove(PLAYER_SETTINGS.CUSTOM_TABLE_TEXTURE)
  storage.remove(PLAYER_SETTINGS.CUSTOM_TABLE_TEXTURE_SCALE)
  storage.remove(PLAYER_SETTINGS.PLAYER_COLOR)
  storage.remove(PLAYER_SETTINGS.PLAYER_NAME)
  storage.remove(PLAYER_SETTINGS.SHOW_PLAYER_NAMES)
  const res = await _api.pub.logout()
  try {
    const data = await res.json()
    if ('success' in data) {
      await init()
      return { error: false }
    }

    return { error: '[2021-09-25 18:36]' }
  } catch {
    return { error: 'Unknown error' }
  }
}

export async function login(
  email: string,
  password: string,
): Promise<{ error: string | false }> {
  const res = await _api.pub.auth(email, password)
  try {
    const data = await res.json()
    if ('success' in data) {
      await init()
      return { error: false }
    }
    return { error: data.reason }
  } catch {
    return { error: 'Unknown error' }
  }
}

async function register(
  username: string,
  email: string,
  password: string,
): Promise<{ error: string | false }> {
  const res = await _api.pub.register(username, email, password)
  try {
    const data = await res.json()
    if ('success' in data) {
      return { error: false }
    }
    return { error: data.reason }
  } catch {
    return { error: 'Unknown error' }
  }
}

async function sendPasswordResetEmail(
  email: string,
): Promise<{ error: string | false }> {
  const res = await _api.pub.sendPasswordResetEmail(email)
  try {
    const data = await res.json()
    if ('success' in data) {
      return { error: false }
    }
    return { error: data.reason }
  } catch {
    return { error: 'Unknown error' }
  }
}

async function changePassword(
  password: string,
  token: string,
): Promise<{ error: string | false }> {
  const res = await _api.pub.changePassword(password, token)
  try {
    const data = await res.json()
    if ('success' in data) {
      return { error: false }
    }
    return { error: data.reason }
  } catch {
    return { error: 'Unknown error' }
  }
}

export const api = {
  logout,
  login,
  register,
  sendPasswordResetEmail,
  changePassword,
}
